import { useEffect } from "react"
import { X } from "lucide-react"

/**
 * Gemeinsame Huelle fuer Popups: Overlay, Panel, Kopfzeile, Schliessen.
 *
 * Im Portal gibt es rund vierzehn Modals, und jedes hat sich sein Overlay
 * selbst gebaut — mit leicht unterschiedlichen Abstaenden, Schatten und
 * Verhalten bei Esc. Neue Popups nutzen diese Huelle; die bestehenden koennen
 * nach und nach nachziehen.
 *
 * Absichtlich schlank: kein Fokus-Trap, kein Portal in den body. Das Portal
 * hat keine verschachtelten Overlays, und beides waere Aufwand ohne Nutzen.
 *
 * ⚠️ `confirm()` blockt CDP — in Modals, die per Browser-Automat getestet
 * werden, gehoert eine echte Rueckfrage ins UI statt in den Browser-Dialog.
 */
export function ModalShell({
  open,
  onClose,
  titel,
  untertitel,
  kopfRechts,
  breite = 900,
  ebene = 200,
  children,
  fuss,
}: {
  open: boolean
  onClose: () => void
  titel: React.ReactNode
  untertitel?: React.ReactNode
  /** Aktionen rechts in der Kopfzeile (Status, Links). */
  kopfRechts?: React.ReactNode
  breite?: number
  /** z-index des Overlays. Nur „Problem melden“ braucht mehr (liegt über allen Pop-ups). */
  ebene?: number
  children: React.ReactNode
  fuss?: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const aufEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", aufEsc)
    // Hintergrund festhalten, sonst scrollt beim Wischen die Seite darunter
    const vorher = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", aufEsc)
      document.body.style.overflow = vorher
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="pp-overlay oben"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: ebene,
        background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        // Auf niedrigen Fenstern (Laptop, #96) weniger Rand — sonst gehen
        // von 720 px schon 80 px fuer Luft drauf.
        padding: "clamp(12px, 5vh, 40px) 20px", overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, width: "100%", maxWidth: breite,
          boxShadow: "0 24px 64px rgba(15,23,42,0.28)",
          display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 2 * clamp(12px, 5vh, 40px))",
        }}
      >
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "16px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", lineHeight: 1.35 }}>
              {titel}
            </div>
            {untertitel && (
              <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                {untertitel}
              </div>
            )}
          </div>
          {kopfRechts && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {kopfRechts}
            </div>
          )}
          <button
            onClick={onClose}
            title="Schließen (Esc)"
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              color: "#94a3b8", padding: 4, flexShrink: 0, lineHeight: 0,
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {children}
        </div>

        {fuss && (
          <div style={{
            padding: "12px 18px", borderTop: "1px solid #e5e7eb", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            background: "#f8fafc", borderRadius: "0 0 14px 14px",
          }}>
            {fuss}
          </div>
        )}
      </div>
    </div>
  )
}
